import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { listReportes } from '../api';
import type { ReporteListItem } from '../api';
import { getCredenciales } from '../api/credenciales';
import { useAuth, canAccessRfc, isAdmin, getUserRfcs } from '../auth';
import { cleanEmpresa } from '../utils/format';
import { EmpresaContext, EMPRESA_TODAS, type EmpresaOption } from './context';

const STORAGE_KEY = 'asi.empresaSeleccionada';

interface Props {
  children: ReactNode;
}

function readStoredRfc(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || EMPRESA_TODAS;
  } catch {
    // localStorage puede lanzar en navegación privada/con cookies bloqueadas.
    return EMPRESA_TODAS;
  }
}

export function EmpresaProvider({ children }: Props) {
  const { user } = useAuth();
  const [reportes, setReportes]               = useState<ReporteListItem[]>([]);
  const [clientesRfcs, setClientesRfcs]       = useState<string[]>([]);
  const [nombresClientes, setNombresClientes] = useState<Record<string, string>>({});
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [rfc, setRfcState]                    = useState<string>(readStoredRfc);
  const [refreshTick, setRefreshTick]         = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listReportes()
      .then(data => {
        if (cancelled) return;
        setReportes(data.filter(r => canAccessRfc(user, r.rfc)));
        setError(null);
      })
      .catch(() => { if (!cancelled) setError('No se Pudieron Cargar las Empresas.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, refreshTick]);

  // Roster de clientes cargados (RFC + nombre) — solo AdminGlobal tiene
  // acceso a /credenciales. Es la fuente real de "qué empresas existen",
  // independiente de si ya corrió una conciliación: antes el selector salía
  // solo de `reportes`, así que una limpieza de datos de conciliación lo
  // dejaba vacío aunque el cliente siguiera dado de alta.
  useEffect(() => {
    if (!isAdmin(user)) return;
    let cancelled = false;
    getCredenciales()
      .then(res => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const c of res.clientes) {
          if (c.nombre_cliente) map[c.rfc.toUpperCase()] = c.nombre_cliente;
        }
        setNombresClientes(map);
        setClientesRfcs(res.clientes.map(c => c.rfc.toUpperCase()));
      })
      .catch(() => { /* Sin roster, se sigue con nombre_empresa del reporte. */ });
    return () => { cancelled = true; };
  }, [user, refreshTick]);

  const setRfc = useCallback((next: string) => {
    setRfcState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Nada que hacer si localStorage no está disponible — la selección
      // simplemente no persiste entre sesiones.
    }
  }, []);

  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  // Base de RFCs: clientes cargados (admin) o los propios del usuario
  // (Contador/Cliente) — no depende de que exista conciliación. `reportes`
  // solo se usa para completar el nombre comercial cuando no hay roster de
  // credenciales (no-admin) y como red de seguridad por si queda algún RFC
  // viejo en reportes que no esté en ninguna de las dos fuentes de arriba.
  const empresas = useMemo<EmpresaOption[]>(() => {
    const nombrePorReporte: Record<string, string> = {};
    for (const r of reportes) {
      if (!nombrePorReporte[r.rfc.toUpperCase()]) nombrePorReporte[r.rfc.toUpperCase()] = r.nombre_empresa || '';
    }

    const rfcsBase = isAdmin(user) ? clientesRfcs : getUserRfcs(user);
    const rfcsReportes = reportes.map(r => r.rfc.toUpperCase()).filter(rfc => canAccessRfc(user, rfc));
    const rfcs = Array.from(new Set([...rfcsBase, ...rfcsReportes]));

    const opciones = rfcs.map(rfc => {
      const nombre = cleanEmpresa(nombresClientes[rfc] || nombrePorReporte[rfc] || '');
      const tieneNombre = nombre && nombre.toUpperCase() !== rfc;
      return { value: rfc, label: tieneNombre ? `${rfc}  —  ${nombre}` : rfc };
    });
    return opciones.sort((a, b) => a.label.localeCompare(b.label));
  }, [reportes, nombresClientes, clientesRfcs, user]);

  // Si el RFC guardado (localStorage, o de una sesión anterior) ya no es
  // accesible para este usuario — cambió de cuenta en el mismo navegador, o
  // le revocaron el permiso — se cae a EMPRESA_TODAS en vez de dejar el
  // selector apuntando a una empresa que ya no puede ver.
  useEffect(() => {
    if (loading) return;
    if (rfc !== EMPRESA_TODAS && !empresas.some(e => e.value === rfc)) setRfc(EMPRESA_TODAS);
  }, [loading, empresas, rfc, setRfc]);

  const value = useMemo(() => ({
    rfc, setRfc, reportes, nombresClientes, empresas, loading, error, refresh,
  }), [rfc, setRfc, reportes, nombresClientes, empresas, loading, error, refresh]);

  return <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>;
}
